import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import SettingsCard from '../../organisms/SettingsCard';
import FormCardGrid from '../../organisms/FormCardGrid';
import CustomFieldTable from '../../organisms/CustomFieldTable';
import Loading from '../../atoms/Loading';
import Button from '../../atoms/Button';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import {
  deleteFormField,
  getFormFields,
  saveFormFields,
} from '../../../redux/complaint/actions';
import { fetchWorkflowSteps } from '../../../redux/Workflow/actions';
import {
  CUSTOM_FIELD_LIBRARY,
  FIELD_ACCESS_OPTIONS,
  FIELD_TYPE_OPTIONS,
  isPrioritiesField,
  isStatusField,
  isLockedStatusField,
  isStoreIdField,
  isDefaultStatusOption,
  sortStatusOptions,
  toUiFieldType,
  withDefaultStatus,
  formHasDuplicateLabelAndType,
  formHasOptionFieldMissingOptions,
  missingOptionsMessage,
} from '../../../data/formFieldPresets';

const FIELD_TYPE_MAP = {
  CUSTOM: 'Custom',
  TEXT: 'Text',
  TEXTAREA: 'Text Area',
  SELECT: 'Select',
  DROPDOWN: 'Dropdown',
  NUMBER: 'Number',
  DATE: 'Date',
  DATETIME: 'Date Time',
  EMAIL: 'Email',
  // MULTISELECT: 'MultiSelect',
  // MULTI_SELECT: 'MultiSelect',
  CHECKBOX: 'Checkbox',
  RADIO: 'Radio',
  // MOBILE: 'Mobile',
  // PHONE: 'Mobile',
  FILE: 'File',
  IMAGES: 'Images',
  TIME: 'Time',
};

const FIELD_ACCESS_MAP = {
  BOTH: 'Both',
  USER: 'User',
  ADMIN: 'Admin',
};

const FIELD_TYPE_TO_API = {
  ...Object.fromEntries(
    Object.entries(FIELD_TYPE_MAP).map(([apiValue, uiValue]) => [uiValue, apiValue]),
  ),
  Select: 'SELECT',
  Dropdown: 'SELECT',
  Radio: 'RADIO',
  // Radio: 'SELECT',
  // MultiSelect: 'MULTI_SELECT',
  // Mobile: 'PHONE',
  Images: 'FILE',
  Time: 'DATETIME',
  'Text Area': 'TEXTAREA',
  'Date Time': 'DATETIME',
  // Textarea: 'TEXTAREA',
  // TextField: 'TEXT',
};

const FIELD_ACCESS_TO_API = {
  ...Object.fromEntries(
    Object.entries(FIELD_ACCESS_MAP).map(([apiValue, uiValue]) => [uiValue, apiValue]),
  ),
  // All: 'BOTH',
  // Staff: 'USER',
  Both: 'BOTH',
  User: 'USER',
  Admin: 'ADMIN',
};

function toFieldKey(label) {
  const words = String(label || '')
    .trim()
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (!words.length) return '';
  return (
    words[0].toLowerCase() +
    words
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('')
  );
}

function toFieldKeyFromLabelAndType(label, fieldType) {
  const labelKey = toFieldKey(label);
  const typeKey = toFieldKey(fieldType);
  if (!labelKey) return '';
  if (!typeKey) return labelKey;
  return labelKey + typeKey.charAt(0).toUpperCase() + typeKey.slice(1);
}

function toSavedFieldKey(field, fieldName) {
  if (isStatusField({ ...field, fieldLabel: fieldName })) return toFieldKey(fieldName) || 'status';
  if (isStoreIdField({ ...field, fieldLabel: fieldName })) return 'storeIdCustom';
  return toFieldKeyFromLabelAndType(fieldName, field.fieldType);
}

function resolveComplaintTypeId(value) {
  const id = value?.data ?? value?.id ?? value;
  if (id == null || id === '') return null;
  const text = String(id).trim();
  if (!text || text === 'null' || text === 'undefined') return null;
  return text;
}

function formFieldsErrorText(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return error.message || error.error || '';
}

function isPersistedField(field){
  if(!field) return false;
  return typeof field.id === 'number' || /^\d+$/.test(String(field.id));
}

function toApiOptions(field= []) {
  const options = isStatusField(field)
    ? sortStatusOptions(field.options || [])
    : (field.options || []);
  if (!options.length) return null;
  const mappings = field.parentMappings || {};
  const colors = field.optionColors || {};
  const hasParents = Boolean(field.dependsOn) || options.some((option) => mappings[option]);

  if (isStatusField(field)) {
    return {
      values: options.map((label, index) => {
        const value = {
          name: label,
          displayOrder: index + 1,
          isActive: true,
          isDefault: isDefaultStatusOption(label),
        };
        if (colors[label]) value.color = colors[label];
        return value;
      }),
    };
  }

  if (hasParents) {
    return {
      values: options.map((label) => {
        const value = { label };
        if (mappings[label]) value.dependsOnValue = mappings[label];
        return value;
      }),
    };
  }
  return { values: options };
}

function toApiFormField(field, allFields = [], workflowSteps = []) {
  const fieldName = String(field.fieldLabel || '').trim();
  const labelUnchanged =
    field.originalFieldName != null &&
    fieldName === String(field.originalFieldName).trim();
  const parent = allFields.find((item) => (
    String(item.fieldLabel || '').trim() === field.dependsOn && field.dependsOn
  ));
  const fieldKey = toSavedFieldKey(field, fieldName) || (labelUnchanged ? field.fieldKey : '');
  const parentKey = parent
    ? (toSavedFieldKey(parent, parent.fieldLabel) || parent.fieldKey)
    : '';
  const hideForWorkflowStepId = resolveHideForWorkflowStepId(field, workflowSteps);
  const options = toApiOptions(field);
  // const options = toApiOptions(field, allFields);
  const fieldType = FIELD_TYPE_TO_API[field.fieldType]
    ?? String(field.fieldType || 'TEXT').toUpperCase();
  const fieldAccess = FIELD_ACCESS_TO_API[field.fieldAccess]
    ?? (field.fieldAccess ? String(field.fieldAccess).toUpperCase() : 'USER');

  const payload = {
    fieldName,
    fieldKey,
    fieldType,
    required: Boolean(field.required),
    displayOrder: Number(field.order) || 1,
    fieldAccess,
    isActive: field.isActive !== false,
  };

  if (hideForWorkflowStepId != null && hideForWorkflowStepId !== '') {
    payload.hideForWorkflowStepId = hideForWorkflowStepId;
  }
  if (options?.values?.length) {
    payload.options = options;
  }
  if (parentKey && parentKey !== fieldKey) {
    payload.dependsOnFieldKey = parentKey;
  }
  return payload;
}

function toFieldEditSnapshot(field) {
  return JSON.stringify({
    fieldLabel: String(field.fieldLabel || '').trim(),
    fieldType: field.fieldType ?? '',
    required: Boolean(field.required),
    fieldAccess: field.fieldAccess ?? '',
    hideFor: [...(field.hideFor || [])].sort(),
    dependsOn: field.dependsOn || '',
    options: field.options || [],
    parentMappings: field.parentMappings || {},
    optionColors: field.optionColors || {},
    order: Number(field.order) || 1,
  });
}

function hasFieldEdits(current, original) {
  return toFieldEditSnapshot(current) !== toFieldEditSnapshot(original);
}

function toPutPayload(fields = [], allFields = fields, workflowSteps = []) {
  const savedFields = fields.filter((field) => (
    !field.isNew && String(field.fieldLabel || '').trim()
  ));
  return {
    fields: savedFields.map((field, index) => (
      toApiFormField({ ...field, order: field.order || index + 1 }, allFields, workflowSteps)
    )),
  };
}

function persistedIdsFrom(...lists) {
  const ids = [];
  const seen = new Set();
  lists.flat().forEach((field) => {
    if (!isPersistedField(field)) return;
    const fieldId = Number(field.id);
    if (seen.has(fieldId)) return;
    seen.add(fieldId);
    ids.push(fieldId);
  });
  return ids;
}

function unwrapWorkflowSteps(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function workflowLevelLabel(step) {
  if (step == null) return null;
  if (typeof step === 'string') return step;
  if (step.levelNo != null && step.levelNo !== '') return `Level ${step.levelNo}`;
  return step.name ?? step.label ?? step.title ?? null;
}

function hideForLabel(step, steps = []) {
  if (!step) return null;
  const fromStep = workflowLevelLabel(step);
  if (fromStep) return fromStep;
  const stepId = typeof step === 'object' ? (step.id ?? step.workflowStepId) : null;
  if (stepId == null) return null;
  const match = steps.find((item) => String(item.id) === String(stepId));
  return workflowLevelLabel(match);
}

function resolveHideForWorkflowStepId(field, steps = []) {
  const labels = Array.isArray(field.hideFor) ? field.hideFor : [];
  if (!labels.length) return null;
  for (const label of labels) {
    const match = steps.find((step) => (
      workflowLevelLabel(step) === label || String(step.id) === String(label)
    ));
    if (match?.id != null) return match.id;
  }
  const fallback = field.hideForWorkflowStepId;
  if (fallback == null || fallback === '') return null;
  const exists = steps.some((step) => String(step.id) === String(fallback));
  return exists ? fallback : null;
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function optionLabel(value) {
  if (typeof value === 'string') return value;
  return value?.label ?? value?.name ?? value?.value ?? '';
}

function optionDependsOnValue(value) {
  if (!value || typeof value !== 'object') return '';
  return value.dependsOnValue ?? value.parent ?? '';
}

function resolveParentOptionLabel(dependsOnValue, parentValues = []) {
  if (!dependsOnValue) return '';
  const parentLabels = parentValues.map(optionLabel).filter(Boolean);
  const exact = parentLabels.find((label) => label === dependsOnValue);
  if (exact) return exact;
  const byKey = parentLabels.find((label) => (
    toFieldKey(label) === dependsOnValue || toFieldKey(label) === toFieldKey(dependsOnValue)
  ));
  return byKey || dependsOnValue;
}

function optionColor(value) {
  if (value && typeof value === 'object' && value.color) return value.color;
  return '';
}

function normalizeFormFields(list = [], workflowSteps = []) {
  const sorted = [...list].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  return withDefaultStatus(sorted.map((item) => {
    const values = item?.options?.values ?? [];
    const parent = sorted.find((field) => (
      field.id === item.dependsOnFieldId
      || (item.dependsOnFieldKey && field.fieldKey === item.dependsOnFieldKey)
    ));
    const hideForStep = workflowSteps.find((step) => (
      String(step.id) === String(item.hideForWorkflowStepId)
    ));

    return {
      id: item.id,
      order: item.displayOrder,
      fieldLabel: item.fieldName ?? item.fieldLabel ?? '',
      fieldType: toUiFieldType(item.fieldType, FIELD_TYPE_MAP, item.fieldKey),
      required: Boolean(item.required),
      fieldAccess: FIELD_ACCESS_MAP[item.fieldAccess] ?? item.fieldAccess ?? '',
      hideFor: uniqueValues([
        ...(Array.isArray(item.hideFor) ? item.hideFor : []),
        hideForLabel(item.hideForWorkflowStep, workflowSteps),
        hideForLabel(hideForStep, workflowSteps),
      ]),
      dependsOn: item.dependsOnField?.fieldName ?? parent?.fieldName ?? item.dependsOn ?? '',
      options: values.map(optionLabel).filter(Boolean),
      parentMappings: Object.fromEntries(
        values
          .map((value) => {
            const label = optionLabel(value);
            const mapped = optionDependsOnValue(value);
            if (!label || !mapped) return null;
            return [label, resolveParentOptionLabel(mapped, parent?.options?.values ?? [])];
          })
          .filter(Boolean),
      ),
      optionColors: Object.fromEntries(
        values
          .map((value) => [optionLabel(value), optionColor(value)])
          .filter(([label, color]) => label && color),
      ),
      fieldKey: item.fieldKey,
      originalFieldName: item.fieldName ?? item.fieldLabel ?? '',
      dependsOnFieldId: item.dependsOnFieldId ?? null,
      hideForWorkflowStepId: item.hideForWorkflowStepId ?? null,
      isActive: item.isActive !== false,
      label: item.fieldName ?? item.fieldLabel ?? '',
    };
  }));
}

export default function FormFieldsStep({ stepMeta, stepperProps }) {
  const dispatch = useDispatch();
  const { systemId } = useParams();
  const storeComplaintTypeId = useSelector((store) => store.appDetails?.id );
  const complaintTypeId = resolveComplaintTypeId(storeComplaintTypeId) ?? resolveComplaintTypeId(systemId);
  const { state, update } = useSettingsWizard();
  const { formFields, prefillFormFields = [] } = state;
  const addExternalFieldRef = useRef(null);
  // Unused: save never sets a saving/deleting field id.
  // const [savingFieldId] = useState(null);
  // const [deletingFieldId, setDeletingFieldId] = useState(null);
  const handleFieldsChange = useCallback((fields) => update({ formFields: fields }), [update]);

  const handleSaveField = useCallback(async (field) => {
    const fieldName = String(field.fieldLabel || '').trim();
    if (!fieldName) return null;
    return {
      ...field,
      fieldLabel: fieldName,
      fieldKey: toSavedFieldKey(field, fieldName),
      originalFieldName: fieldName,
      isNew: false,
    };
  }, []);

  const handleDeleteField = useCallback(async (field) => {
    if(isLockedStatusField(field)) return false;
    if(!isPersistedField(field)) return true;

    // setDeletingFieldId(field.id);
    try{
      await dispatch(deleteFormField({
        complaintTypeId,
        fieldId: field.id,
      })).unwrap();
      return true;
    } finally {
      // setDeletingFieldId(null);
    }
  }, [complaintTypeId, dispatch]);

  const apiFields = useSelector((store) => store.complaintAppComplaint?.formFields?.data);
  const isLoading = useSelector((store) => store.complaintAppComplaint?.formFields?.isLoading);
  const error = useSelector((store) => store.complaintAppComplaint?.formFields?.error);
  const workflowStepsRaw = useSelector((store) => store.workflow?.workflowSteps);
  const workflowSteps = useMemo(
    () => unwrapWorkflowSteps(workflowStepsRaw),
    [workflowStepsRaw],
  );
  const workflowStepsRef = useRef(workflowSteps);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    workflowStepsRef.current = workflowSteps;
  }, [workflowSteps]);

  useEffect(() => {
    const workflowTypeId = resolveComplaintTypeId(complaintTypeId);
    if (workflowTypeId == null) return undefined;
    dispatch(fetchWorkflowSteps(workflowTypeId));
    return undefined;
  }, [complaintTypeId, dispatch]);

  useEffect(() => {
    const typeId = resolveComplaintTypeId(complaintTypeId);
    if (typeId == null) {
      return undefined;
    }
    let cancelled = false;
    dispatch(getFormFields(typeId))
      .unwrap()
      .then((payload) => {
        if (cancelled) return;
        const list = Array.isArray(payload) ? payload : payload?.data;
        const hasSavedFields = Array.isArray(list) && list.length > 0;
        if (!hasSavedFields && prefillFormFields.length) {
          const cloned = normalizeFormFields(prefillFormFields, workflowStepsRef.current).map((field, index) => ({
            ...field,
            id: `prefill-${index}`,
            isNew: false,
          }));
          update({ formFields: withDefaultStatus(cloned) });
        } else {
          const mapped = withDefaultStatus(
            normalizeFormFields(Array.isArray(list) ? list : [], workflowStepsRef.current),
          );
          update({ formFields: mapped });
        }
        setHasLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        if (prefillFormFields.length) {
          const cloned = normalizeFormFields(prefillFormFields, workflowStepsRef.current).map((field, index) => ({
            ...field,
            id: `prefill-${index}`,
            isNew: false,
          }));
          update({ formFields: withDefaultStatus(cloned) });
        }
        setHasLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [complaintTypeId, dispatch, prefillFormFields, update]);

  const mappedFields = useMemo(
    () => withDefaultStatus(normalizeFormFields(apiFields, workflowSteps)),
    [apiFields, workflowSteps],
  );
  const typeId = resolveComplaintTypeId(complaintTypeId);
  const fieldsReady = typeId == null || hasLoaded;
  const gridFields = fieldsReady ? formFields : mappedFields;
  const customLibrary = useMemo(() => (
    CUSTOM_FIELD_LIBRARY.filter((item) => !gridFields.some((field) => (
      (item.fieldKey && field.fieldKey === item.fieldKey)
      || (isStatusField(item) && isStatusField(field))
      || (isPrioritiesField(item) && isPrioritiesField(field))
      || (isStoreIdField(item) && isStoreIdField(field))
    )))
  ), [gridFields]);
  const hasUnresolvedDuplicateField = useMemo(
    () => formHasDuplicateLabelAndType(gridFields),
    [gridFields],
  );
  const hasMissingOptions = useMemo(
    () => formHasOptionFieldMissingOptions(gridFields),
    [gridFields],
  );
  const errorText = formFieldsErrorText(error);
  const fieldTypeOptions = FIELD_TYPE_OPTIONS;
  const fieldAccessOptions = FIELD_ACCESS_OPTIONS;
  const hideForOptions = useMemo(() => {
    const fromApi = [...workflowSteps]
      .sort((left, right) => (left.levelNo ?? 0) - (right.levelNo ?? 0))
      .map((step) => workflowLevelLabel(step))
      .filter(Boolean);
    const fromFields = mappedFields.flatMap((field) => field.hideFor);
    return uniqueValues([...fromApi, ...fromFields]);
  }, [mappedFields, workflowSteps]);

  const pendingSaveFields = useMemo(() => (
    gridFields.filter((field) => field.isNew && String(field.fieldLabel || '').trim())
  ), [gridFields]);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);

  const persistAndGoNext = useCallback(async (includePending) => {
    if (formHasOptionFieldMissingOptions(gridFields)) return;
    if (!complaintTypeId) return;
    try {
      const preparedFields = gridFields.map((field) => {
        if (!includePending || !field.isNew) return field;
        const fieldName = String(field.fieldLabel || '').trim();
        if (!fieldName) return field;
        return {
          ...field,
          fieldLabel: fieldName,
          fieldKey: toSavedFieldKey(field, fieldName),
          originalFieldName: fieldName,
          isNew: false,
        };
      });
      const savedFields = preparedFields.filter((field) => (
        !field.isNew && String(field.fieldLabel || '').trim()
      ));
      const serverById = new Map(
        normalizeFormFields(apiFields, workflowSteps).map((field) => [Number(field.id), field]),
      );
      const hasChanges = savedFields.some((field) => {
        if (!isPersistedField(field)) return true;
        const original = serverById.get(Number(field.id));
        return !original || hasFieldEdits(field, original);
      });

      if (!hasChanges) {
        stepperProps.onNext?.();
        return;
      }

      const orderedFields = [...savedFields].sort(
        (left, right) => Number(Boolean(left.dependsOn)) - Number(Boolean(right.dependsOn)),
      );
      const serverIds = persistedIdsFrom(apiFields, savedFields);

      for (const fieldId of serverIds) {
        try {
          await dispatch(deleteFormField({ complaintTypeId, fieldId })).unwrap();
        } catch {
          // Already removed on the server; local JSON is still the source of truth.
        }
      }
      await dispatch(saveFormFields({
        complaintTypeId,
        data: toPutPayload(orderedFields, orderedFields, workflowSteps),
      })).unwrap();
      const refreshedPayload = await dispatch(getFormFields(complaintTypeId)).unwrap();
      const refreshedList = Array.isArray(refreshedPayload)
        ? refreshedPayload
        : refreshedPayload?.data;
      update({
        formFields: withDefaultStatus(
          normalizeFormFields(Array.isArray(refreshedList) ? refreshedList : [], workflowSteps),
        ),
      });
      stepperProps.onNext?.();
    } catch {
      // Stay on Form Fields; error is stored on formFields.error.
    }
  }, [apiFields, complaintTypeId, dispatch, gridFields, stepperProps, update, workflowSteps]);

  const handleNext = useCallback(() => {
    if (formHasOptionFieldMissingOptions(gridFields)) return;
    if (pendingSaveFields.length) {
      setPendingModalOpen(true);
      return;
    }
    persistAndGoNext(false);
  }, [gridFields, pendingSaveFields.length, persistAndGoNext]);

  return (
    <>
      <Stepper {...stepperProps} steps={SETTINGS_STEPS} onNext={handleNext} />
      {pendingModalOpen ? (
        <div
          className="ca-confirm-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="form-fields-pending-save-title"
        >
          <div className="ca-confirm-modal__dialog">
            <p id="form-fields-pending-save-title" className="ca-confirm-modal__message">
              Some fields are pending to save.
            </p>
            <div className="ca-confirm-modal__actions">
              <Button
                variant="outline"
                onClick={() => {
                  setPendingModalOpen(false);
                  persistAndGoNext(false);
                }}
              >
                OK
              </Button>
              <Button
                onClick={() => {
                  setPendingModalOpen(false);
                  persistAndGoNext(true);
                }}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <SettingsCard 
      className="ca-settings-card--plain"
      title={stepMeta.title}
      description={stepMeta.description}
      showNext={false}
    >
      {(!fieldsReady || isLoading) ? (
        <Loading text="Loading form fields" />
      ) : errorText && !gridFields.length ? (
        <div className="ca-validation-toast" role="alert">{errorText}</div>
      ) : (
      <div className="ca-form-fields">
        {errorText ? <div className="ca-validation-toast" role="alert">{errorText}</div> : null}
        <CustomFieldTable
          fields={customLibrary}
          disableAdd={hasUnresolvedDuplicateField || hasMissingOptions}
          disableAddHint={
            hasUnresolvedDuplicateField
              ? 'Resolve duplicate field first'
              : hasMissingOptions
                ? missingOptionsMessage(gridFields)
                : undefined
          }
          onAddField={(field) => addExternalFieldRef.current?.({ ...field })}
        />
        <FormCardGrid
          initialFields={gridFields}
          addExternalFieldRef={addExternalFieldRef}
          onChange={handleFieldsChange}
          fieldTypeOptions={fieldTypeOptions}
          fieldAccessOptions={fieldAccessOptions}
          hideForOptions={hideForOptions}
          // savingFieldId={savingFieldId}
          // deletingFieldId={deletingFieldId}
          onSave={handleSaveField}
          onDelete={handleDeleteField}
        />
      </div>
      )}
      </SettingsCard>
    </>
  );
}
